import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext.jsx';
import { useSettings } from '../context/SettingsContext.jsx';

// Bell icon in the header. Shows unread count and a dropdown with recent notifications.
export default function NotificationBell() {
  const { apiFetch } = useAuth();
  const { t } = useSettings();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const boxRef = useRef(null);

  const load = async () => {
    try {
      const res = await apiFetch('/notifications');
      if (res?.ok) {
        const data = await res.json();
        setItems(data.notifications || []);
        setUnread(data.unread || 0);
      }
    } catch { /* ignore */ }
  };

  // Poll periodically + refresh when the user returns to the tab (cheap "near-real-time"
  // without a WebSocket: most freshness comes from re-checking on focus/visibility).
  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    const onFocus = () => load();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      // mark all read when opening
      try { await apiFetch('/notifications/read-all', { method: 'PUT' }); } catch {}
      setUnread(0);
      setItems(prev => prev.map(n => ({ ...n, read: true })));
    }
  };

  const remove = async (id) => {
    try { await apiFetch(`/notifications/${id}`, { method: 'DELETE' }); } catch {}
    setItems(prev => prev.filter(n => n.id !== id));
  };

  // Delete every notification at once.
  const clearAll = async () => {
    try { await apiFetch('/notifications', { method: 'DELETE' }); } catch { /* ignore */ }
    setItems([]); setUnread(0);
  };

  // Follow back from a "X followed you" notification.
  const followBack = async (n) => {
    try {
      const res = await apiFetch('/social/follow/' + n.from_username, { method: 'POST' });
      if (res?.ok) setItems(prev => prev.map(x => x.id === n.id ? { ...x, i_follow_back: true } : x));
    } catch { /* ignore */ }
  };

  const timeAgo = (iso) => {
    const d = (Date.now() - new Date(iso).getTime()) / 1000;
    if (d < 60) return t('notif_now');
    if (d < 3600) return Math.floor(d / 60) + t('notif_min');
    if (d < 86400) return Math.floor(d / 3600) + t('notif_hour');
    return Math.floor(d / 86400) + t('notif_day');
  };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <button onClick={toggle} className="tap" title={t('notif_title')} aria-label={t('notif_title')}
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '16px', position: 'relative', padding: '4px' }}>
        🔔
        {unread > 0 && (
          <span style={{ position: 'absolute', top: '-2px', right: '-2px', background: 'var(--app-bad,#ef4444)', color: '#fff',
                         borderRadius: '10px', fontSize: '9px', fontWeight: 'bold', minWidth: '15px', height: '15px',
                         display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '40px', right: '0', width: '290px', maxHeight: '380px', overflowY: 'auto',
                      background: 'var(--app-card,#121216)', border: '1px solid #ffffff15', borderRadius: '10px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 1000, padding: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px' }}>
            <span style={{ fontSize: '11px', letterSpacing: '1px', color: '#8a8a92', fontWeight: 'bold' }}>{t('notif_title')}</span>
            {items.length > 0 && (
              <button onClick={clearAll}
                style={{ background: 'transparent', border: 'none', color: '#7a8290', fontSize: '11px', cursor: 'pointer', padding: '2px 4px' }}>
                {t('notif_clear_all')}
              </button>
            )}
          </div>
          {items.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--app-dimmer,#5a5a62)', fontSize: '12px' }}>{t('notif_empty')}</div>
          )}
          {items.map(n => (
            <div key={n.id} style={{ padding: '10px', borderRadius: '8px', marginBottom: '3px',
                                     background: n.read ? 'transparent' : 'var(--app-bg,#0d0d10)',
                                     border: '1px solid #ffffff08', position: 'relative' }}>
              <div style={{ fontSize: '12px', color: '#ece6d6', lineHeight: '1.4', paddingRight: '18px' }}>
                {n.type === 'follow'
                  ? <><b>@{n.from_username}</b> {t('notif_followed_you')}</>
                  : n.text}
              </div>
              {n.type === 'follow' && (
                n.i_follow_back
                  ? <div style={{ fontSize: '11px', color: 'var(--app-good,#22c55e)', marginTop: '7px', fontWeight: 600 }}>🤝 {t('notif_youre_friends')}</div>
                  : <button onClick={() => followBack(n)}
                      style={{ marginTop: '8px', background: 'var(--app-accent,#c9a24b)', color: '#fff', border: 'none',
                               borderRadius: '7px', padding: '5px 11px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                      {t('notif_follow_back')}
                    </button>
              )}
              <div style={{ fontSize: '10px', color: 'var(--app-dimmer,#5a5a62)', marginTop: '4px' }}>{timeAgo(n.created_at)}</div>
              <button onClick={() => remove(n.id)} aria-label={t('delete')}
                style={{ position: 'absolute', top: '6px', right: '6px', background: 'transparent', border: 'none',
                         color: 'var(--app-dimmer,#5a5a62)', cursor: 'pointer', fontSize: '13px' }}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
